import type { ComponentProps, SyntheticEvent } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BottomProps,
  BoxShadowProps,
  FlexProps,
  SpaceProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  backgroundColor,
  bottom,
  boxShadow,
  display,
  flex,
  space,
  width,
  zIndex,
} from 'styled-system';

import { useLocalization } from '../../../providers/LocaleProvider';
import { useRouter } from '../../../providers/RouterProvider';
import type { BoxSizingProps } from '../../../theme/system-utilities';
import { boxSizing } from '../../../theme/system-utilities';
import { trackApplyFilters } from '../../../tracking/filters';
import { getProductsListPath } from '../../../utils/url';
import { ButtonLink } from '../../Button';

interface DrawerFooterProps {
  url: string;
  disabled: boolean;
  onNavigate?: () => void;
}

const UnstyledFooter = styled.div<
  BoxSizingProps &
    BackgroundColorProps &
    BoxShadowProps &
    BottomProps &
    WidthProps &
    SpaceProps &
    FlexProps &
    ZIndexProps
>`
  ${boxSizing};
  ${backgroundColor};
  ${boxShadow};
  ${bottom};
  ${width};
  ${space};
  ${flex};
  ${zIndex};
`;

const Footer = (props: ComponentProps<typeof UnstyledFooter>) => (
  <UnstyledFooter
    boxShadow="0 1px 8px 0 rgba(0, 0, 0, 0.15)"
    boxSizing="border-box"
    backgroundColor="sys.neutral.background.default"
    width="100%"
    px="sp_16"
    py="sp_8"
    flex={1}
    zIndex={1}
    {...props}
  />
);

const UnstyledApplyLink = styled(ButtonLink)<any>`
  ${display};
  ${space};
`;

const ApplyLink = (props: ComponentProps<typeof UnstyledApplyLink>) => (
  <UnstyledApplyLink data-testid="apply-button" display="flex" {...props} />
);

const DrawerFooter = ({ url, disabled, onNavigate }: DrawerFooterProps) => {
  const { t } = useLocalization();
  const router = useRouter();

  const onClick = (event: SyntheticEvent) => {
    event.preventDefault();

    if (disabled) return;

    const path = getProductsListPath(url);
    trackApplyFilters(url);
    onNavigate?.();
    router.replace(path, { scroll: true });
  };

  return (
    <Footer>
      <ApplyLink
        href={url}
        disabled={disabled}
        onClick={onClick}
        aria-label={
          disabled ? t('h24_filter_button_no_filters') : t('h24_filter_button_apply_label')
        }
      >
        {t('h24_apply')}
      </ApplyLink>
    </Footer>
  );
};

export default DrawerFooter;
